import { updateSlideInDeck } from "../editor/deckEditor.js";
import { getLayerCutout } from "./slideImageService.js";
import { pollSurfImageJob } from "./surfImageJob.js";
import { applySurfJobToDeck } from "./surfJobAppliers.js";
import { submitEditSlideImage } from "./surfJobSubmits.js";
import { bakedTextAltText, getLayerTextRender, resolveBakedTextForLayer, } from "../illustration/bakedIllustration.js";
import { getTextRasterAssetKey, textRasterAltText, } from "../illustration/textRaster.js";
import { deckBackgroundColor, getRasterBackdrop, solidBackdropPromptLines, } from "../illustration/rasterBackdrop.js";
const TEXT_RASTER_EDIT_SUFFIX = " Preserve the flat solid backdrop color outside the subject — no checkerboard or gray/white grid.";
export function resolveImageAssetKey(deck, target) {
    const slide = deck.slides.find((s) => s.id === target.slideId);
    if (!slide)
        return null;
    if (target.kind === "background") {
        return slide.background.s3Key?.trim() || null;
    }
    if (target.kind === "textRaster") {
        const layer = slide.layers.find((l) => l.id === target.layerId);
        if (!layer || layer.type !== "text")
            return null;
        return getTextRasterAssetKey(layer) ?? null;
    }
    const layer = slide.layers.find((l) => l.id === target.layerId);
    if (!layer || layer.type !== "image")
        return null;
    const content = layer.content?.trim();
    // Only a real s3 key (contains "/"), not the "Immagine" placeholder.
    return content && content.includes("/") ? content : null;
}
export function getImageReferenceKey(deck, target) {
    const slide = deck.slides.find((s) => s.id === target.slideId);
    if (!slide)
        return undefined;
    if (target.kind === "background")
        return slide.background.referenceS3Key?.trim() || undefined;
    if (target.kind === "textRaster") {
        const layer = slide.layers.find((l) => l.id === target.layerId);
        const raw = layer?.style?.imageReferenceKey;
        return typeof raw === "string" && raw.trim() ? raw.trim() : undefined;
    }
    const layer = slide.layers.find((l) => l.id === target.layerId);
    const raw = layer?.style?.imageReferenceKey;
    return typeof raw === "string" && raw.trim() ? raw.trim() : undefined;
}
export function setImageReferenceKey(deck, target, referenceS3Key) {
    return updateSlideInDeck(deck, target.slideId, (slide) => {
        if (target.kind === "background") {
            return {
                ...slide,
                background: {
                    ...slide.background,
                    referenceS3Key: referenceS3Key?.trim() || undefined,
                },
            };
        }
        if (target.kind === "textRaster") {
            return {
                ...slide,
                layers: slide.layers.map((l) => l.id === target.layerId
                    ? {
                        ...l,
                        style: {
                            ...l.style,
                            imageReferenceKey: referenceS3Key?.trim() || undefined,
                        },
                    }
                    : l),
            };
        }
        return {
            ...slide,
            layers: slide.layers.map((l) => l.id === target.layerId
                ? {
                    ...l,
                    style: {
                        ...l.style,
                        imageReferenceKey: referenceS3Key?.trim() || undefined,
                    },
                }
                : l),
        };
    });
}
export function setRefineInstructions(deck, target, instructions) {
    const stored = instructions.length > 0 ? instructions : undefined;
    return updateSlideInDeck(deck, target.slideId, (slide) => {
        if (target.kind === "background") {
            return {
                ...slide,
                background: {
                    ...slide.background,
                    refineInstructions: stored,
                },
            };
        }
        if (target.kind === "textRaster") {
            return {
                ...slide,
                layers: slide.layers.map((l) => l.id === target.layerId
                    ? {
                        ...l,
                        style: {
                            ...l.style,
                            refineInstructions: stored,
                        },
                    }
                    : l),
            };
        }
        return {
            ...slide,
            layers: slide.layers.map((l) => l.id === target.layerId
                ? {
                    ...l,
                    style: {
                        ...l.style,
                        refineInstructions: stored,
                    },
                }
                : l),
        };
    });
}
export function getRefineInstructions(deck, target) {
    const slide = deck.slides.find((s) => s.id === target.slideId);
    if (!slide)
        return "";
    if (target.kind === "background") {
        return slide.background.refineInstructions ?? "";
    }
    if (target.kind === "textRaster") {
        const layer = slide.layers.find((l) => l.id === target.layerId);
        const raw = layer?.style?.refineInstructions;
        return typeof raw === "string" ? raw : "";
    }
    const layer = slide.layers.find((l) => l.id === target.layerId);
    const raw = layer?.style?.refineInstructions;
    return typeof raw === "string" ? raw : "";
}
export function buildEditPrompt(deck, target, instructions) {
    const slide = deck.slides.find((s) => s.id === target.slideId);
    let cutout = false;
    let bakedNote = "";
    if (target.kind === "layer") {
        const layer = slide?.layers.find((l) => l.id === target.layerId);
        if (layer?.type === "image") {
            cutout = getLayerCutout(layer);
            if (getLayerTextRender(layer) === "in_image" && slide) {
                bakedNote = ` Preserve legible baked text: ${bakedTextAltText(resolveBakedTextForLayer(layer, slide))}.`;
            }
        }
    }
    if (target.kind === "textRaster" && slide) {
        const layer = slide.layers.find((l) => l.id === target.layerId);
        if (layer?.type === "text") {
            bakedNote = ` Preserve legible baked text: ${textRasterAltText(layer)}.`;
        }
    }
    const textRaster = target.kind === "textRaster";
    const mode = target.kind === "background" || slide?.visualBrief?.imageRole === "background"
        ? "presentation slide BACKGROUND"
        : "illustrative CONTENT image";
    let prompt = [
        `Refine this ${mode} per user instructions.`,
        "Preserve overall composition unless instructions say otherwise.",
        bakedNote || "No new readable text, logos, or fabricated data unless explicitly requested.",
        `Instructions: ${instructions.trim()}`,
    ].join(" ");
    if (cutout && slide && target.kind === "layer") {
        const layer = slide.layers.find((l) => l.id === target.layerId);
        const backdrop = layer?.type === "image"
            ? getRasterBackdrop(layer, deck.theme)
            : deckBackgroundColor(deck.theme);
        prompt += ` ${solidBackdropPromptLines(backdrop).join(" ")}`;
    }
    if (textRaster && slide) {
        const layer = slide.layers.find((l) => l.id === target.layerId);
        if (layer?.type === "text") {
            const backdrop = getRasterBackdrop(layer, deck.theme);
            prompt += ` ${solidBackdropPromptLines(backdrop).join(" ")}`;
            prompt += TEXT_RASTER_EDIT_SUFFIX;
        }
    }
    return prompt;
}
/** S3 blob keys always contain a `/`; placeholders like "Immagine" do not. */
function isAssetKeyLike(value) {
    return typeof value === "string" && value.trim().includes("/");
}
export function collectInputKeys(currentKey, referenceKey) {
    const keys = [];
    for (const k of [currentKey, referenceKey]) {
        const trimmed = k?.trim();
        if (isAssetKeyLike(trimmed) && !keys.includes(trimmed))
            keys.push(trimmed);
    }
    return keys.slice(0, 4);
}
/** IMAGE_EDIT refine for slide background or image widget (F18). */
export async function editSlideImage(cfg, deck, target, instructions, opts) {
    const { submit, deck: prepared } = await submitEditSlideImage(cfg, deck, target, instructions, opts);
    const outputAssetKey = await pollSurfImageJob(cfg, submit.jobId);
    return applySurfJobToDeck(prepared, {
        op: submit.op,
        target: submit.target,
        output: { kind: "asset", assetKey: outputAssetKey },
        applyMeta: submit.applyMeta,
    });
}
